import { Button, Form } from "react-bootstrap";
import { React, useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { Formik } from "formik";
import * as Yup from "yup";
import { getBlogsById, updateBlogsById } from "../../api/axios";
import CmsCircularProgress from "../../Components/Common/CircularProgress";
import removeIcon from "../../Assets/remove-image-photo.svg";
import { showToast } from "../../Utills/toast";
import FormGroup from "../../Components/Common/FormGroup";

// Validation schema
const validationSchema = Yup.object({
  blog_img: Yup.mixed().required("Image is required"),
  blog_title: Yup.string().required("Title is required"),
  blog_content: Yup.string().required("Content is required"),
  blog_readMore: Yup.string().required("Read More is required"),
});

const EditBlog = () => {
  const { id, partnerId } = useParams();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [initialData, setInitialData] = useState({
    blog_img: "",
    blog_title: "",
    blog_content: "",
    blog_readMore: "",
    original_blog_img: "",
  });
  const inputRef = useRef(null);
  const formikRef = useRef(null);

  const fetchBlogData = async (blogId) => {
    setIsLoading(true);
    try {
      const res = await getBlogsById(blogId);
      const blogData = res.data.data[0];
      
      
      const formData = {
        blog_img: blogData.blog_img || "",
        blog_title: blogData.blog_title || "",
        blog_content: blogData.blog_content || "",
        blog_readMore: blogData.blog_readMore || "",
        original_blog_img: blogData.blog_img || "", // Track original image for fallback
      };
      
      setInitialData(formData);
      
      // Reset Formik with new data if component is mounted
      if (formikRef.current) {
        formikRef.current.resetForm({
          values: formData
        });
      }
    } catch (err) {
      showToast({
        type: 'error',
        message: 'Failed to fetch blog data. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogData(id);
  }, [id]);

  const handleImageChange = (event, setFieldValue) => {
    const file = event.target.files[0];
    if (file) {
      setFieldValue("blog_img", file);
    }
  };

  const handleImageDelete = (setFieldValue, values) => {
    // For new images: clear the temporary upload and fall back to original image if exists
    if (values.original_blog_img) {
      setFieldValue("blog_img", values.original_blog_img);
    } else {
      setFieldValue("blog_img", "");
    }
    
    // Reset file input
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleExistingImageDelete = (setFieldValue) => {
    // For existing images: clear the image from state and show upload button
    setFieldValue("blog_img", "");
    setFieldValue("original_blog_img", "");
    
    // Reset file input
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleSubmit = async (values, {setSubmitting}) => {
    setSaving(true);

    // Additional validation for character limits
    if (values.blog_title.length > 50) {
      showToast({
        type: 'error',
        message: 'Title must be less than 50 characters',
      });
      setSaving(false);
      return;
    }

    if (values.blog_content.length > 300) {
      showToast({
        type: 'error',
        message: 'Content must be less than 300 characters',
      });
      setSaving(false);
      return;
    }

    const formData = new FormData();
    formData.append("blog_title", values.blog_title);
    formData.append("blog_content", values.blog_content);
    formData.append("blog_readMore", values.blog_readMore);

    // Add updated image data to formData if it exists
    if (values.blog_img instanceof File) {
      formData.append("blog_img", values.blog_img);
    }

    try {
      const res = await updateBlogsById(id, formData);
      console.log(res);
      
      if (res.status === 200) {
        // fetchBlogData(id);
        setInitialData(prev => ({
          ...prev,
          blog_img: res.data.user.blog_img || prev.blog_img,
          blog_title: res.data.user.blog_title || prev.blog_title,
          blog_content: res.data.user.blog_content || prev.blog_content,
          blog_readMore: res.data.user.blog_readMore || prev.blog_readMore,
          original_blog_img: res.data.user.blog_img || prev.original_blog_img
        }));
        
        showToast({
          type: 'success',
          message: res.data.message || 'Blog has been updated successfully!',
        });
      } else {
        showToast({
          type: 'error',
          message: res.data.message || 'Failed to update blog. Please try again.',
        });
      }
      setSaving(false);
    } catch (err) {
      showToast({
        type: 'error',
        message: err.data.message || 'Failed to update blog. Please try again.',
      });
      console.log(err);
      setSaving(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClick = () => {
    navigate(`/${partnerId}/resources`);
  };

  // const getInitialValues = () => ();

  return (
    <>
      {isLoading ? <CmsCircularProgress/> :
        <FormWrapper>
          <Formik
            innerRef={formikRef}
            initialValues={initialData}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({ handleSubmit, getFieldProps, setFieldValue, values, errors, touched }) => (
              <Form onSubmit={handleSubmit} noValidate>
                {console.log({values})}
                <Title>Edit Blog {initialData.blog_title}</Title>
                <SliderDiv>
                  {/* Hidden field to track original image */}
                  <input type="hidden" name="original_blog_img" value={values.original_blog_img} />
                  
                  {/* Image Upload Section */}
                  <FormGroup 
                    label='Image' 
                    requiredField
                    error={touched.blog_img && errors.blog_img}
                  >
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageChange(e, setFieldValue)}
                      style={{ display: "none" }}
                      ref={inputRef}
                      name="blog_img"
                    />
                    
                    {/* Show uploaded image (File object) - NEW IMAGE */}
                    {values.blog_img instanceof File ? (
                      <ImageWrapper>
                        <UploadImage>
                          <BootstrapImage
                            src={URL.createObjectURL(values.blog_img)}
                            alt="blog_img"
                            onClick={() => inputRef.current.click()}
                            onMouseEnter={(e) => {
                              e.target.style.opacity = 0.8;
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.opacity = 1;
                            }}
                          />
                        </UploadImage>
                        {/* Always show delete icon for new images */}
                        {values.blog_img && values.blog_img && values.blog_img instanceof File && (
                          <RemoveButtonWrapper>
                            <BootstrapButton
                              type="button"
                              onClick={() => handleImageDelete(setFieldValue, values)}
                            >
                              <img src={removeIcon} alt="removeImage" height={'20px'} width={'20px'}/>
                            </BootstrapButton>
                          </RemoveButtonWrapper>
                        )}
                      </ImageWrapper>
                    ) : (
                      values.blog_img && typeof values.blog_img === 'string' ? (
                        <ImageWrapper>
                          <UploadImage>
                            <BootstrapImage
                              src={values.blog_img}
                              alt="existing-blog-img"
                              onClick={() => inputRef.current.click()}
                              onMouseEnter={(e) => {
                                e.target.style.opacity = 0.8;
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.opacity = 1;
                              }}
                            />
                          </UploadImage>
                          {/* Always show delete icon for existing images
                          {values.blog_img && values.blog_img  &&  (
                            <RemoveButtonWrapper>
                              <BootstrapButton
                                type="button"
                                onClick={() => handleExistingImageDelete(setFieldValue)}
                              >
                                <img src={removeIcon} alt="removeImage" height={'20px'} width={'20px'}/>
                              </BootstrapButton>
                            </RemoveButtonWrapper>
                          )} */}
                        </ImageWrapper>
                      ) : (
                        /* Show upload button when no image - NO IMAGE */
                        <Button 
                          type="button"
                          className="mb-0"
                          onClick={() => inputRef.current.click()}
                          variant={touched.blog_img && errors.blog_img ? "danger" : "primary"}
                        > 
                          Upload the Image
                        </Button>
                      )
                    )}
                  </FormGroup>

                  {/* Title Field */}
                  <FormGroup 
                    label='Title'
                    requiredField
                    error={touched.blog_title && errors.blog_title}
                  >
                    <Form.Control
                      type="text"
                      placeholder="Enter Blog Title"
                      maxLength="50"
                      {...getFieldProps("blog_title")}
                      isInvalid={touched.blog_title && errors.blog_title}
                    />
                  </FormGroup>

                  {/* Content Field */}
                  <FormGroup 
                    label='Content'
                    requiredField
                    error={touched.blog_content && errors.blog_content}
                  >
                    <Form.Control
                      as="textarea"
                      rows={5}
                      placeholder="Enter Blog Content"
                      maxLength="300"
                      {...getFieldProps("blog_content")}
                      isInvalid={touched.blog_content && errors.blog_content}
                    />
                  </FormGroup>

                  {/* Read More Field */}
                  <FormGroup 
                    label='Read More'
                    requiredField
                    error={touched.blog_readMore && errors.blog_readMore}
                  >
                    <Form.Control
                      as="textarea"
                      rows={5}
                      placeholder="Enter Blog Read More"
                      {...getFieldProps("blog_readMore")}
                      isInvalid={touched.blog_readMore && errors.blog_readMore}
                    />
                  </FormGroup>

                  {/* Button Section */}
                  <ButtonWrapper className="mt-3">
                    <Button variant="secondary" onClick={handleClick}>
                      Go Back
                    </Button>
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={saving}
                    >
                      {saving ? "Saving..." : "Save Changes"}
                    </Button>
                  </ButtonWrapper>
                </SliderDiv>
              </Form>
            )}
          </Formik>
        </FormWrapper>
      }
    </>
  );
};

export default EditBlog;

const FormWrapper = styled.div`
  @media (min-width:576px) {
   padding: 1rem;
  }
    @media (min-width:768px) {
   padding: 1.5rem;
  }
   padding: .5rem;
`;

const Title = styled.h3`
  text-align: center;
  padding: 10px 0;
`;

const ButtonWrapper = styled.div`
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
   @media (max-width: 890px) {
    gap: 2rem;
  }
  @media (max-width: 425px) {
    gap: 1rem;
  }
`;
const ImageWrapper = styled.div`
    position:relative;
    max-width: 15vw;
   @media (max-width:576px) {
   max-width: 90vw;
  }
`;

const RemoveButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  position: absolute;
    top: -17px;
    right: -14px;
    @media (max-width:576px) {
     top: -9px;
    right: -17px;
  }
`;
const UploadImage = styled.div`
  max-width: 15vw;
   overflow: hidden;
  border-radius: 6px;
  @media (max-width:576px) {
   max-width: 90vw;
  }    
`;
const SliderDiv = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
`;
const BootstrapImage = styled('img')(({ theme }) => ({
  maxWidth: "15vw",
  cursor: "pointer",
  "@media (max-width: 576px)": {
    maxWidth: "90vw",
  },
}));
const  BootstrapButton = styled('button')(({ theme }) => ({
  margin: '0 !important',
  backgroundColor:'#FFFFFF',
  padding: '5px 7px',
  border: '1px solid #dc3545',
  "&:hover": {
    background: '#fffeff',
    boxShadow:'rgba(0, 0, 0, 0.2) 0px 2px 4px -1px, rgba(0, 0, 0, 0.14) 0px 4px 5px 0px, rgba(0, 0, 0, 0.12) 0px 1px 10px 0px'
  },
  '@media (max-width:576px)': {
    padding: '2px 4px',
  },
}));